import { useCallback, useEffect, useRef, useState } from 'react'

import { useBridge } from '@onl-web/shared/bridge'

import { getToken, setToken } from '@/apis/tokenStore'
import { apiGetUserInfo } from '@/payment/api'

import { MAX_LOG_COUNT, TYPE_LABELS } from './lib/constants'
import type { LogEntry } from './lib/types'
import {
  ButtonRow,
  ClearButton,
  Container,
  Desc,
  EmptyLog,
  FormGroup,
  InfoItem,
  InfoLabel,
  InfoRow,
  InfoValue,
  Input,
  LogItem,
  LogList,
  LogMessage,
  LogMeta,
  LogSection,
  LogTime,
  LogType,
  Notice,
  PrimaryButton,
  Section,
  SectionTitle,
  StatusCard,
  StatusDot,
  StatusLabel,
  StatusText,
  StatusValue,
  SubLabel,
  SubTitle,
  Title,
} from './styles'

let logId = 0

/**
 * payment webview의 Flutter ↔ JS 브릿지 디버그 페이지.
 *
 * 브릿지 핸들러 규약 v2 기준 — getAuthToken / getDeviceInfo / onSyncError /
 * onNext / onPrevious 를 직접 호출해 응답을 확인할 수 있도록 만든 페이지.
 *
 * dev 환경에 배포해서 Flutter 팀이 실제 앱에서 브릿지 연동을 검증.
 */
export default function DebugPage() {
  const { status, isReady } = useBridge({ timeoutMs: 10000 })
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [nextPath, setNextPath] = useState('/service-area/search')
  const [prevPages, setPrevPages] = useState('1')
  const [customName, setCustomName] = useState('')
  const [customPayload, setCustomPayload] = useState('')
  const [manualToken, setManualToken] = useState('')
  const prevStatusRef = useRef(status)

  const addLog = useCallback((type: LogEntry['type'], message: string) => {
    const time = new Date().toLocaleTimeString('ko-KR', { hour12: false, fractionalSecondDigits: 3 })
    setLogs((prev) => [{ id: ++logId, time, type, message }, ...prev].slice(0, MAX_LOG_COUNT))
  }, [])

  useEffect(() => {
    if (prevStatusRef.current === status) return
    prevStatusRef.current = status
    if (status === 'ready') addLog('connect', '브릿지 연결됨')
    if (status === 'timeout') addLog('error', '브릿지 연결 타임아웃 (10초 내 응답 없음)')
  }, [status, addLog])

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      addLog('event', `Flutter 이벤트 수신 → ${JSON.stringify(detail)}`)
    }
    window.addEventListener('flutterEvent', handler)
    return () => window.removeEventListener('flutterEvent', handler)
  }, [addLog])

  const rawCall = async (name: string, args?: unknown) => {
    if (!window.flutter_inappwebview) {
      throw new Error('브릿지 연결 안 됨')
    }
    const result =
      args !== undefined
        ? await window.flutter_inappwebview.callHandler(name, args)
        : await window.flutter_inappwebview.callHandler(name)
    if (typeof result === 'string') {
      try {
        return JSON.parse(result)
      } catch {
        return result
      }
    }
    return result
  }

  const call = async (name: string, args?: unknown) => {
    addLog('info', `→ ${name}${args !== undefined ? ` (${JSON.stringify(args)})` : ''}`)
    const start = performance.now()
    try {
      const result = await rawCall(name, args)
      const elapsed = Math.round(performance.now() - start)
      addLog('response', `${name} (${elapsed}ms) → ${JSON.stringify(result)}`)
      return result
    } catch (error) {
      addLog('error', `${name} 실패 — ${error instanceof Error ? error.message : String(error)}`)
    }
  }

  const callGetAuthToken = () => call('getAuthToken')
  const callGetDeviceInfo = () => call('getDeviceInfo')
  const callOnSyncError = () => call('onSyncError', { message: '디버그 페이지 테스트', code: 'DEBUG_TEST' })

  // mobile-api GET /user — 응답 전체를 로그로 찍어 주소가 어느 필드로 오는지 확인
  const callGetUserInfo = async () => {
    addLog('info', '→ GET /user (mobile-api)')
    const start = performance.now()
    try {
      const user = await apiGetUserInfo()
      const elapsed = Math.round(performance.now() - start)
      addLog('response', `GET /user (${elapsed}ms) → ${JSON.stringify(user)}`)
    } catch (error) {
      addLog('error', `GET /user 실패 — ${error instanceof Error ? error.message : String(error)}`)
    }
  }

  const callOnNext = (type: 'app' | 'webview') => {
    const path = nextPath.trim()
    if (!path) return
    return call('onNext', { type, params: { path } })
  }

  const callOnPrevious = () => {
    const n = parseInt(prevPages, 10)
    if (Number.isNaN(n) || n <= 0) {
      return call('onPrevious')
    }
    return call('onPrevious', { pages: n })
  }

  const callCustom = async () => {
    const name = customName.trim()
    if (!name) return
    let args: unknown
    if (customPayload.trim()) {
      try {
        args = JSON.parse(customPayload)
      } catch {
        addLog('error', 'payload JSON 파싱 실패')
        return
      }
    }
    return call(name, args)
  }

  return (
    <Container>
      <Title>브릿지 연결 테스트 (payment)</Title>
      <SubTitle>핸들러 규약 v2 — getAuthToken / getDeviceInfo / onSyncError / onNext / onPrevious</SubTitle>

      <StatusCard status={status}>
        <StatusDot status={status} />
        <StatusText>
          <StatusLabel>연결 상태</StatusLabel>
          <StatusValue>
            {status === 'connecting' && '연결 대기 중...'}
            {status === 'ready' && '연결됨'}
            {status === 'timeout' && '연결 실패 (브라우저에서 직접 열린 경우 정상)'}
          </StatusValue>
        </StatusText>
      </StatusCard>

      <InfoRow>
        <InfoItem>
          <InfoLabel>브릿지 객체</InfoLabel>
          <InfoValue ok={!!window.flutter_inappwebview}>
            {window.flutter_inappwebview ? '존재' : '없음'}
          </InfoValue>
        </InfoItem>
        <InfoItem>
          <InfoLabel>isReady</InfoLabel>
          <InfoValue ok={isReady}>{isReady ? 'true' : 'false'}</InfoValue>
        </InfoItem>
      </InfoRow>

      {/* 로그 영역을 상단으로 — 호출 결과를 곧바로 확인 가능. 항상 보이게 */}
      <LogSection>
        <SectionTitle>
          로그 ({logs.length})
          {logs.length > 0 && <ClearButton onClick={() => setLogs([])}>초기화</ClearButton>}
        </SectionTitle>
        <LogList>
          {logs.length === 0 && (
            <EmptyLog>
              아직 기록이 없습니다.
              <br />
              아래 버튼을 눌러 핸들러를 호출하면 결과와 Flutter 이벤트가 여기 찍힙니다.
            </EmptyLog>
          )}
          {logs.map((log) => (
            <LogItem
              key={log.id}
              type={log.type}
            >
              <LogMeta>
                <LogTime>{log.time}</LogTime>
                <LogType type={log.type}>{TYPE_LABELS[log.type]}</LogType>
              </LogMeta>
              <LogMessage>{log.message}</LogMessage>
            </LogItem>
          ))}
        </LogList>
      </LogSection>

      <Notice>
        💡 버튼은 항상 활성화됩니다. <strong>브릿지가 없어도 일단 호출 시도</strong>하고
        결과/에러를 위 로그에 찍습니다. (브라우저에서 열면 "브릿지 연결 안 됨" 에러가 정상)
      </Notice>

      <Section>
        <SectionTitle>getAuthToken</SectionTitle>
        <Desc>앱 accessToken / refreshToken 을 조회합니다.</Desc>
        <ButtonRow>
          <PrimaryButton onClick={callGetAuthToken}>getAuthToken 호출</PrimaryButton>
        </ButtonRow>
        <Desc style={{ marginTop: 12 }}>
          ⚠ 임시(브라우저 검증용) — 토큰 수동 주입. 현재 저장된 토큰:{' '}
          <code>{getToken() ? `${getToken()?.slice(0, 12)}...` : '(없음)'}</code>
        </Desc>
        <ButtonRow>
          <input
            type='text'
            value={manualToken}
            onChange={(e) => setManualToken(e.target.value)}
            placeholder='accessToken 붙여넣기'
            style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
          />
          <PrimaryButton
            onClick={() => {
              if (!manualToken.trim()) {
                addLog('error', '토큰 입력값이 비어있습니다')
                return
              }
              setToken(manualToken.trim())
              addLog('info', `토큰 주입됨 (${manualToken.trim().slice(0, 12)}...)`)
              setManualToken('')
            }}
          >
            토큰 적용
          </PrimaryButton>
          <PrimaryButton
            onClick={() => {
              setToken('')
              addLog('info', '토큰 클리어')
            }}
          >
            클리어
          </PrimaryButton>
        </ButtonRow>
      </Section>

      <Section>
        <SectionTitle>getDeviceInfo</SectionTitle>
        <Desc>safe-area padding(top/bottom) + 화면 크기를 조회합니다.</Desc>
        <ButtonRow>
          <PrimaryButton onClick={callGetDeviceInfo}>getDeviceInfo 호출</PrimaryButton>
        </ButtonRow>
      </Section>

      <Section>
        <SectionTitle>유저정보 조회 (mobile-api GET /user)</SectionTitle>
        <Desc>주소 등록 여부 판별용. 응답 전체를 로그에 찍어 주소가 어느 필드로 오는지 확인.</Desc>
        <ButtonRow>
          <PrimaryButton onClick={callGetUserInfo}>GET /user 호출</PrimaryButton>
        </ButtonRow>
      </Section>

      <Section>
        <SectionTitle>onSyncError</SectionTitle>
        <Desc>웹 동기 오류를 앱 로그로 전달. (응답: isSuccess: true)</Desc>
        <ButtonRow>
          <PrimaryButton onClick={callOnSyncError}>샘플 onSyncError 호출</PrimaryButton>
        </ButtonRow>
      </Section>

      <Section>
        <SectionTitle>onNext</SectionTitle>
        <Desc>다음 화면 이동. type=app(앱 딥링크) / type=webview(새 webview push)</Desc>
        <FormGroup>
          <SubLabel>params.path</SubLabel>
          <Input
            value={nextPath}
            onChange={(e) => setNextPath(e.target.value)}
            placeholder='/service-area/search · collect ...'
          />
          <ButtonRow>
            <PrimaryButton
              onClick={() => callOnNext('app')}
              disabled={!nextPath.trim()}
            >
              type=app 호출
            </PrimaryButton>
            <PrimaryButton
              onClick={() => callOnNext('webview')}
              disabled={!nextPath.trim()}
            >
              type=webview 호출
            </PrimaryButton>
          </ButtonRow>
        </FormGroup>
      </Section>

      <Section>
        <SectionTitle>onPrevious</SectionTitle>
        <Desc>앱 화면 뒤로가기. pages 미입력 시 1페이지.</Desc>
        <FormGroup>
          <SubLabel>pages</SubLabel>
          <Input
            value={prevPages}
            onChange={(e) => setPrevPages(e.target.value)}
            placeholder='1, 2, 3 ...'
            inputMode='numeric'
          />
          <PrimaryButton onClick={callOnPrevious}>onPrevious 호출</PrimaryButton>
        </FormGroup>
      </Section>

      <Section>
        <SectionTitle>자유 호출</SectionTitle>
        <Desc>임의 handler 이름·payload로 직접 호출 (테스트용).</Desc>
        <FormGroup>
          <Input
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder='handler 이름'
          />
          <Input
            value={customPayload}
            onChange={(e) => setCustomPayload(e.target.value)}
            placeholder='payload JSON (선택)'
          />
          <PrimaryButton
            onClick={callCustom}
            disabled={!customName.trim()}
          >
            호출
          </PrimaryButton>
        </FormGroup>
      </Section>
    </Container>
  )
}

